import axios from "axios";
import { toast } from "sonner";

// Blank means same-origin, which is required when the production FastAPI
// process serves both the React build and /api routes.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_WORKSPACE_ID = "ws_7f2a9c4e1d68";
const LEGACY_DEFAULT_WORKSPACE_ID = "ws_default";

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

export const isApiRequestCanceled = (error) =>
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

// Attach the currently selected workspace to every data request.
api.interceptors.request.use((config) => {
  const url = config.url || "";
  if (!url.startsWith("/auth") && !url.startsWith("/workspaces")) {
    const storedWorkspaceId = localStorage.getItem("workspace_id");
    const ws = storedWorkspaceId === LEGACY_DEFAULT_WORKSPACE_ID
      ? DEFAULT_WORKSPACE_ID
      : (storedWorkspaceId || DEFAULT_WORKSPACE_ID);
    if (storedWorkspaceId === LEGACY_DEFAULT_WORKSPACE_ID) {
      localStorage.setItem("workspace_id", DEFAULT_WORKSPACE_ID);
    }
    config.params = { ...(config.params || {}), workspace_id: ws };
  }
  return config;
});

// Redirect to login on 401 responses from non-auth endpoints.
// This catches session loss caused by backend restarts or idle timeouts.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.startsWith("/auth")) {
      // Dismiss any "Connection lost" banner before redirecting so both
      // never appear simultaneously (e.g. after a backend restart that
      // briefly caused a network gap before returning 401).
      toast.dismiss("connection-lost");
      window.location.replace("/login?reason=session_expired");
    }
    return Promise.reject(error);
  }
);

// Convert any API error into a plain, human-readable string.
// FastAPI validation errors (422) return `detail` as an ARRAY of objects
// ({type, loc, msg, input, ctx}) which must never be rendered directly in React.
const FIELD_LABELS = { email: "Email", password: "Password", name: "Name" };

export const apiErrorMessage = (e, fallback = "Something went wrong. Please try again.") => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (!d || typeof d.msg !== "string") return "";
        const rawField = Array.isArray(d.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        const field = FIELD_LABELS[rawField] || rawField;
        return field ? `${field}: ${d.msg}` : d.msg;
      })
      .filter(Boolean);
    if (msgs.length) return msgs.join(" · ");
  }
  if (detail && typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return fallback;
};

export default api;
